import React, { useEffect, useState } from "react";
import { Alert, Button, Spinner } from "reactstrap";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { createApplicationFeePaymentIntent } from "../../helpers/paymentsApi";

const STRIPE_PUBLISHABLE_KEY = import.meta.env.VITE_APP_STRIPE_PUBLISHABLE_KEY;
const stripePromise = STRIPE_PUBLISHABLE_KEY ? loadStripe(STRIPE_PUBLISHABLE_KEY) : null;

const money = (cents) => `$${(Number(cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 0 })}`;

/** Renders inside <Elements> — needs useStripe/useElements, so it can't
 * mount before the client secret (and therefore the Elements provider) exists. */
const FeePaymentForm = ({ amountLabel, applicantEmail, onSuccess, onError }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || submitting) return;
    setSubmitting(true);
    onError("");
    try {
      const { error, paymentIntent } = await stripe.confirmPayment({ elements, redirect: "if_required" });
      if (error) {
        onError(error.message || "Payment failed. Please try again.");
        return;
      }
      if (paymentIntent?.status === "succeeded" || paymentIntent?.status === "processing") {
        onSuccess(paymentIntent.id);
      } else {
        onError("Payment could not be completed. Please try a different payment method.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement options={{ defaultValues: { billingDetails: { email: applicantEmail || "" } } }} />
      <div className="text-muted d-flex align-items-center gap-1 mt-3" style={{ fontSize: "0.7rem" }}>
        <i className="bx bx-lock-alt" />
        Payments are processed securely by Stripe.
      </div>
      <Button color="primary" block className="mt-3" type="submit" disabled={!stripe || submitting}>
        {submitting ? <Spinner size="sm" /> : `Pay ${amountLabel}`}
      </Button>
    </form>
  );
};

/**
 * Collects the one-time rental application fee for a draft application that
 * doesn't become real (visible to the property owner) until this succeeds.
 * Mounts its own PaymentIntent on open, tied to the still-pending draft.
 */
const ApplicationFeePayment = ({ pendingApplicationId, applicantEmail, onSuccess, onCancel }) => {
  const [clientSecret, setClientSecret] = useState("");
  const [amount, setAmount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [setupError, setSetupError] = useState("");
  const [payError, setPayError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setSetupError("");
      try {
        const res = await createApplicationFeePaymentIntent({ pendingApplicationId });
        if (cancelled) return;
        setClientSecret(res.clientSecret);
        setAmount(res.amount || 0);
      } catch (e) {
        console.error("Create application fee payment intent error:", e);
        if (!cancelled) setSetupError(e.message || "Couldn't start the application fee payment.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [pendingApplicationId]);

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner color="primary" />
      </div>
    );
  }

  if (setupError) {
    return (
      <>
        <Alert color="danger" className="py-2 small">{setupError}</Alert>
        {onCancel && <Button color="light" size="sm" onClick={onCancel}>Back</Button>}
      </>
    );
  }

  if (!stripePromise || !clientSecret) {
    return <Alert color="danger" className="py-2 small mb-0">Payments aren&apos;t configured yet.</Alert>;
  }

  return (
    <Elements stripe={stripePromise} options={{ clientSecret, appearance: { theme: "stripe" } }}>
      {payError && <Alert color="danger" className="py-2 small">{payError}</Alert>}
      <FeePaymentForm amountLabel={money(amount)} applicantEmail={applicantEmail} onSuccess={onSuccess} onError={setPayError} />
    </Elements>
  );
};

export default ApplicationFeePayment;
